import { useState, useMemo, useCallback } from 'react';
import { useDiscord } from './hooks/useDiscord';
import { useParty } from './hooks/useParty';
import { useLayout } from './hooks/useLayout';
import { getAvatarUrl, getInstanceId } from './discord';
import Layout from './components/Layout';
import type { ViewType } from './party';

export default function App() {
  const { auth, loading, error } = useDiscord();
  const layout = useLayout();
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string>('person');

  const partyOptions = useMemo(() => {
    if (!auth) return null;
    return {
      roomId: auth.channelId || auth.guildId || getInstanceId(),
      userId: auth.user.id,
      username: auth.user.global_name || auth.user.username,
      avatar: getAvatarUrl(auth.user),
    };
  }, [auth]);

  const party = useParty(partyOptions);

  const handleSelectEntity = useCallback((type: string, id: string) => {
    if (!id) {
      // Closing / deselecting
      setSelectedSlug(null);
      setSelectedType('person');
      party.selectEntity(null);
    } else {
      setSelectedType(type);
      setSelectedSlug(id);
      party.selectEntity({ type, id });
    }
  }, [party]);

  const handleChangeView = useCallback((view: ViewType) => {
    party.changeView(view);
  }, [party]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Connecting to Discord...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-screen">
        <h2>Connection Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <Layout
      layout={layout}
      view={party.state.view}
      onChangeView={handleChangeView}
      selectedSlug={selectedSlug}
      selectedType={selectedType}
      onSelectEntity={handleSelectEntity}
      party={party}
      auth={auth}
    />
  );
}
